import { useCallback, useEffect, useRef, useState } from "react";
import { supabase, safeChannel } from "./supabase";

// usePendingKidReviews — cook_logs where the kid is listed in
// diners[] AND there's no cook_log_reviews row from this kid yet.
// Powers the "How was it?" rating card on the kid's Pet tab.
//
// Implementation note:
//   cook_logs.diners is uuid[]. Postgrest supports the "cs" filter
//   (contains) on arrays, and we use it to limit the server-side
//   scan. The reviews join filters out cooks we've already rated.
//
// Realtime: subscribes to cook_logs INSERT (fresh cook flows
// through within seconds) and cook_log_reviews INSERT (submitting
// our own review clears the prompt instantly).

export function usePendingKidReviews(kidUserId, { lookbackHours = 48 } = {}) {
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const chRef = useRef(null);

  const load = useCallback(async () => {
    if (!kidUserId) {
      setPending([]);
      setLoading(false);
      return;
    }
    setLoading(true);

    const sinceIso = new Date(Date.now() - lookbackHours * 3600 * 1000).toISOString();

    // Cooks within the lookback where this kid is a diner.
    const { data: logs, error: logErr } = await supabase
      .from("cook_logs")
      .select("id, recipe_slug, recipe_title, recipe_emoji, cooked_at, user_id, diners")
      .gte("cooked_at", sinceIso)
      .contains("diners", [kidUserId])
      .order("cooked_at", { ascending: false });

    if (logErr) {
      console.warn("[kid-reviews] cook_logs:", logErr.message);
      setError(logErr);
      setPending([]);
      setLoading(false);
      return;
    }
    const logRows = logs || [];
    if (logRows.length === 0) {
      setPending([]);
      setLoading(false);
      return;
    }

    // Already-reviewed by this kid? Exclude.
    const ids = logRows.map((l) => l.id);
    const { data: existingReviews } = await supabase
      .from("cook_log_reviews")
      .select("cook_log_id")
      .eq("reviewer_id", kidUserId)
      .in("cook_log_id", ids);
    const reviewed = new Set((existingReviews || []).map((r) => r.cook_log_id));

    setPending(logRows.filter((l) => !reviewed.has(l.id)));
    setLoading(false);
  }, [kidUserId, lookbackHours]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!kidUserId) return;
    const ch = safeChannel(`rt:kid_pending_reviews:${kidUserId}`)
      .on("postgres_changes",
        { event: "INSERT", schema: "public", table: "cook_logs" },
        () => load())
      .on("postgres_changes",
        { event: "INSERT", schema: "public", table: "cook_log_reviews", filter: `reviewer_id=eq.${kidUserId}` },
        () => load())
      .subscribe();
    chRef.current = ch;
    return () => {
      if (chRef.current) {
        supabase.removeChannel(chRef.current);
        chRef.current = null;
      }
    };
  }, [kidUserId, load]);

  return { pending, loading, error, refresh: load };
}
